import { useState } from 'react';
import { Loader2, Search, ChevronDown, ChevronRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

function Collapsible({ title, badge, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border rounded-lg p-3">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 text-sm font-medium hover:underline w-full text-left"
      >
        {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        {title}
        {badge}
      </button>
      {open && <div className="mt-3 ml-6 space-y-2">{children}</div>}
    </div>
  );
}

function Kv({ label, value, mono = false }) {
  return (
    <div className="flex gap-2 text-sm">
      <span className="text-muted-foreground min-w-[220px] shrink-0">{label}:</span>
      <span className={mono ? 'font-mono' : ''}>{value ?? 'n/a'}</span>
    </div>
  );
}

function fmt(v) {
  if (v == null) return 'n/a';
  const n = typeof v === 'number' ? v : parseFloat(v);
  if (isNaN(n)) return String(v);
  return n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function BookingTable({ rows, label }) {
  if (!rows || rows.length === 0) return <p className="text-sm text-muted-foreground">None</p>;
  return (
    <div className="overflow-x-auto">
      <p className="text-xs text-muted-foreground mb-1">{label}</p>
      <table className="w-full text-xs">
        <thead className="bg-muted/50">
          <tr>
            <th className="text-left p-1.5">Booking</th>
            <th className="text-left p-1.5">Client</th>
            <th className="text-right p-1.5">Value (GBP)</th>
            {rows[0]?.invoiced_gbp != null && <th className="text-right p-1.5">Invoiced</th>}
            {rows[0]?.gap_gbp != null && <th className="text-right p-1.5">Gap</th>}
            {rows[0]?.surplus_gbp != null && <th className="text-right p-1.5">Surplus</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t">
              <td className="p-1.5 font-mono">{r.booking_id}</td>
              <td className="p-1.5">{r.client}</td>
              <td className="p-1.5 text-right font-mono">{fmt(r.booking_value_gbp)}</td>
              {r.invoiced_gbp != null && <td className="p-1.5 text-right font-mono">{fmt(r.invoiced_gbp)}</td>}
              {r.gap_gbp != null && <td className="p-1.5 text-right font-mono text-red-600">{fmt(r.gap_gbp)}</td>}
              {r.surplus_gbp != null && <td className="p-1.5 text-right font-mono text-amber-600">{fmt(r.surplus_gbp)}</td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function ReconAuditPanel() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setLoading(true);
    setResult(null);
    try {
      const { data } = await diagnosticsAPI.reconAudit();
      setResult(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const r = result;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Search className="h-5 w-5 text-blue-600" />
          Reconciliation Audit (Read-Only)
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Maps every reconciliation surface render path, reconciles the three invoiced totals,
          decomposes the booking-vs-invoiced gap, and diagnoses the EUR penny-rounding bug.
          Read-only, no writes, no behaviour changes.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <Button onClick={run} disabled={loading} size="sm">
          {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Search className="h-4 w-4 mr-2" />}
          Run Audit
        </Button>

        {r && (
          <div className="space-y-3">
            <div className="text-xs text-muted-foreground">
              Generated {r.generated_at} | {r.period} | {r.display_currency}
            </div>

            {/* Q0 */}
            <Collapsible
              title="Q0: Build Verification"
              badge={<Badge variant="outline" className="ml-2 text-xs">{r.q0_build_verification?.build_commit_source}</Badge>}
              defaultOpen
            >
              <Kv label="BUILD_COMMIT" value={r.q0_build_verification?.build_commit} mono />
              <Kv label="Source" value={r.q0_build_verification?.build_commit_source} />
              <p className="text-xs text-muted-foreground mt-2">{r.q0_build_verification?.note}</p>
            </Collapsible>

            {/* Q1 */}
            <Collapsible title="Q1: Render Path Traces" defaultOpen>
              {r.q1_render_paths?.key_finding && (
                <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded p-2 mb-3">
                  <p className="text-xs font-medium text-amber-800 dark:text-amber-300">Key Finding</p>
                  <p className="text-xs text-amber-700 dark:text-amber-400 mt-1">{r.q1_render_paths.key_finding}</p>
                </div>
              )}
              {['surface_1_summary_cards', 'surface_2_per_booking_rows', 'surface_3_invoices_vs_payments', 'surface_4_invoices_page'].map((key) => {
                const s = r.q1_render_paths?.[key];
                if (!s) return null;
                const label = key.replace(/_/g, ' ').replace('surface ', 'Surface ');
                return (
                  <Collapsible key={key} title={label}>
                    {Object.entries(s).map(([k, v]) => (
                      <Kv key={k} label={k.replace(/_/g, ' ')} value={typeof v === 'object' ? JSON.stringify(v) : String(v)} mono={k.includes('api') || k.includes('backend') || k.includes('component')} />
                    ))}
                  </Collapsible>
                );
              })}
            </Collapsible>

            {/* Q2 */}
            <Collapsible title="Q2: Invoiced Totals Reconciliation" defaultOpen>
              <div className="overflow-x-auto">
                <table className="w-full text-xs border">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-left p-2">Surface</th>
                      <th className="text-right p-2">Count</th>
                      <th className="text-right p-2">Total (GBP)</th>
                      <th className="text-left p-2">Collection</th>
                      <th className="text-left p-2">Date Filter</th>
                      <th className="text-left p-2">Status Filter</th>
                      <th className="text-left p-2">Includes Supplier</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      {
                        name: 'BVS Summary Cards',
                        data: r.q2_invoiced_totals?.surface_1_bvs_total_invoiced,
                      },
                      {
                        name: 'Payments Tab',
                        data: r.q2_invoiced_totals?.surface_3_payments_tab,
                      },
                      {
                        name: 'Invoices Page',
                        data: r.q2_invoiced_totals?.surface_4_invoices_page,
                      },
                    ].map((row, i) => (
                      <tr key={i} className="border-t">
                        <td className="p-2 font-medium">{row.name}</td>
                        <td className="p-2 text-right font-mono">{row.data?.invoice_count}</td>
                        <td className="p-2 text-right font-mono">{fmt(row.data?.total_gbp)}</td>
                        <td className="p-2 text-xs">{row.data?.collection}</td>
                        <td className="p-2 text-xs">{row.data?.date_filter || 'none'}</td>
                        <td className="p-2 text-xs">{row.data?.status_filter || 'none'}</td>
                        <td className="p-2 text-xs">
                          {row.data?.includes_supplier_invoices === true ? 'Yes' : row.data?.includes_supplier_invoices === false ? 'No' : 'n/a'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {r.q2_invoiced_totals?.divergence_explanation && (
                <div className="mt-2 space-y-1">
                  <p className="text-xs font-medium">Divergence Explanation</p>
                  {Object.entries(r.q2_invoiced_totals.divergence_explanation).map(([k, v]) => (
                    <p key={k} className="text-xs text-muted-foreground"><span className="font-medium">{k.replace(/_/g, ' ')}:</span> {v}</p>
                  ))}
                </div>
              )}
            </Collapsible>

            {/* Q3 */}
            <Collapsible title="Q3: Gap Decomposition" defaultOpen>
              {r.q3_gap_decomposition && (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    <div className="bg-muted/50 rounded p-2">
                      <p className="text-xs text-muted-foreground">Total Bookings</p>
                      <p className="font-mono text-sm font-bold">{fmt(r.q3_gap_decomposition.total_bookings_gbp)}</p>
                    </div>
                    <div className="bg-muted/50 rounded p-2">
                      <p className="text-xs text-muted-foreground">BVS Invoiced</p>
                      <p className="font-mono text-sm font-bold">{fmt(r.q3_gap_decomposition.total_bvs_invoiced_gbp)}</p>
                    </div>
                    <div className="bg-muted/50 rounded p-2">
                      <p className="text-xs text-muted-foreground">Displayed Gap</p>
                      <p className="font-mono text-sm font-bold text-amber-600">{fmt(r.q3_gap_decomposition.displayed_gap_gbp)}</p>
                    </div>
                    <div className="bg-muted/50 rounded p-2">
                      <p className="text-xs text-muted-foreground">Booking Count</p>
                      <p className="font-mono text-sm font-bold">{r.q3_gap_decomposition.booking_count}</p>
                    </div>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full text-xs border">
                      <thead className="bg-muted/50">
                        <tr>
                          <th className="text-left p-2">Bucket</th>
                          <th className="text-right p-2">Count</th>
                          <th className="text-right p-2">Amount (GBP)</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-t">
                          <td className="p-2">Zero-invoice bookings (genuinely uninvoiced)</td>
                          <td className="p-2 text-right font-mono">{r.q3_gap_decomposition.gap_decomposition?.zero_invoice_bookings?.count}</td>
                          <td className="p-2 text-right font-mono text-red-600">{fmt(r.q3_gap_decomposition.gap_decomposition?.zero_invoice_bookings?.total_gbp)}</td>
                        </tr>
                        <tr className="border-t">
                          <td className="p-2">Under-invoiced bookings (partial)</td>
                          <td className="p-2 text-right font-mono">{r.q3_gap_decomposition.gap_decomposition?.under_invoiced_bookings?.count}</td>
                          <td className="p-2 text-right font-mono text-red-600">{fmt(r.q3_gap_decomposition.gap_decomposition?.under_invoiced_bookings?.total_gap_gbp)}</td>
                        </tr>
                        <tr className="border-t">
                          <td className="p-2">Over-invoiced bookings</td>
                          <td className="p-2 text-right font-mono">{r.q3_gap_decomposition.gap_decomposition?.over_invoiced_bookings?.count}</td>
                          <td className="p-2 text-right font-mono text-green-600">{fmt(r.q3_gap_decomposition.gap_decomposition?.over_invoiced_bookings?.total_surplus_gbp)}</td>
                        </tr>
                        <tr className="border-t">
                          <td className="p-2">Fully matched</td>
                          <td className="p-2 text-right font-mono">{r.q3_gap_decomposition.gap_decomposition?.fully_matched_count}</td>
                          <td className="p-2 text-right font-mono">-</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  {/* Population artifacts */}
                  {r.q3_gap_decomposition.population_artifact && (
                    <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded p-2">
                      <p className="text-xs font-medium text-blue-800 dark:text-blue-300">Population Artifacts (inflate BVS invoiced, shrink displayed gap)</p>
                      <Kv
                        label="Non-booking-linked invoices in BVS"
                        value={`${r.q3_gap_decomposition.population_artifact.bvs_includes_non_booking_linked_invoices?.count} invoices, ${fmt(r.q3_gap_decomposition.population_artifact.bvs_includes_non_booking_linked_invoices?.total_gbp)} GBP`}
                      />
                      <Kv
                        label="Supplier invoices in BVS"
                        value={`${r.q3_gap_decomposition.population_artifact.bvs_includes_supplier_invoices?.count} invoices, ${fmt(r.q3_gap_decomposition.population_artifact.bvs_includes_supplier_invoices?.total_gbp)} GBP`}
                      />
                    </div>
                  )}

                  {/* Real gap estimate */}
                  {r.q3_gap_decomposition.real_uninvoiced_estimate && (
                    <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded p-2">
                      <p className="text-xs font-medium text-amber-800 dark:text-amber-300">Real Uninvoiced Estimate</p>
                      <Kv label="Zero-invoice" value={`${fmt(r.q3_gap_decomposition.real_uninvoiced_estimate.zero_invoice_gbp)} GBP`} mono />
                      <Kv label="Under-invoiced" value={`${fmt(r.q3_gap_decomposition.real_uninvoiced_estimate.under_invoiced_gbp)} GBP`} mono />
                      <Kv label="Less over-invoiced" value={`${fmt(r.q3_gap_decomposition.real_uninvoiced_estimate.less_over_invoiced_gbp)} GBP`} mono />
                      <Kv label="Net real gap" value={`${fmt(r.q3_gap_decomposition.real_uninvoiced_estimate.net_real_gap_gbp)} GBP`} mono />
                      <p className="text-xs text-muted-foreground mt-1">{r.q3_gap_decomposition.real_uninvoiced_estimate.note}</p>
                    </div>
                  )}

                  {/* Top uninvoiced bookings */}
                  <BookingTable
                    rows={r.q3_gap_decomposition.gap_decomposition?.zero_invoice_bookings?.top_10}
                    label="Top 10 uninvoiced bookings (by value)"
                  />
                  <BookingTable
                    rows={r.q3_gap_decomposition.gap_decomposition?.under_invoiced_bookings?.top_10}
                    label="Top 10 under-invoiced bookings (by gap)"
                  />
                </div>
              )}
            </Collapsible>

            {/* Q4 */}
            <Collapsible title="Q4: EUR Penny Rounding" defaultOpen>
              {r.q4_eur_penny_rounding && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Badge variant={r.q4_eur_penny_rounding.bug_confirmed ? 'destructive' : 'outline'}>
                      {r.q4_eur_penny_rounding.bug_confirmed ? 'Bug Confirmed' : 'Not Confirmed'}
                    </Badge>
                  </div>
                  <Kv label="EUR to GBP rate" value={r.q4_eur_penny_rounding.eur_to_gbp_rate} mono />
                  <Kv label="GBP to EUR rate (inverse)" value={r.q4_eur_penny_rounding.gbp_to_eur_rate} mono />
                  <p className="text-xs text-muted-foreground">{r.q4_eur_penny_rounding.cause}</p>
                  <p className="text-xs text-muted-foreground">{r.q4_eur_penny_rounding.location}</p>
                  <p className="text-xs font-medium mt-2">Fix: {r.q4_eur_penny_rounding.fix_description}</p>

                  {r.q4_eur_penny_rounding.roundtrip_examples && (
                    <div className="overflow-x-auto mt-2">
                      <table className="w-full text-xs border">
                        <thead className="bg-muted/50">
                          <tr>
                            <th className="text-right p-1.5">Original EUR</th>
                            <th className="text-right p-1.5">To GBP</th>
                            <th className="text-right p-1.5">Back to EUR</th>
                            <th className="text-right p-1.5">Error</th>
                          </tr>
                        </thead>
                        <tbody>
                          {r.q4_eur_penny_rounding.roundtrip_examples.map((ex, i) => (
                            <tr key={i} className={`border-t ${ex.has_error ? 'bg-red-50 dark:bg-red-950/20' : ''}`}>
                              <td className="p-1.5 text-right font-mono">{fmt(ex.original_eur)}</td>
                              <td className="p-1.5 text-right font-mono">{fmt(ex.to_gbp)}</td>
                              <td className="p-1.5 text-right font-mono">{fmt(ex.back_to_eur)}</td>
                              <td className="p-1.5 text-right font-mono">{ex.has_error ? <span className="text-red-600">{fmt(ex.error)}</span> : '0.00'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </Collapsible>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
